/**
 * SuccessLane — About page (/about)
 *
 * Matches the landing-page design language (Poppins + purple palette from
 * artifact 33bd7d1f), and implements the 6 sections + company info from the
 * Landing Page Implementation Spec v2 About-page addendum.
 *
 * IMPORTANT (per spec): no mention of AI writing / AI drafts anywhere on this
 * page. The narrative is: a group of recruiters who build profiles the way the
 * industry actually needs them.
 */
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Design tokens (mirrors the landing page's `c` object)
const c = {
  text:      '#1a1130',
  textMid:   '#4b4560',
  textSoft:  '#5b5470',
  textMute:  '#6b6580',
  textDim:   '#9691a8',
  purpleSoft:'#8a7fb0',
  purple:    '#7c3aed',
  purpleDeep:'#6d28d9',
  purpleMid: '#8b5cf6',
  gradient:  'linear-gradient(135deg, #8b5cf6, #7c3aed)',
  hilite:    'linear-gradient(90deg, #c4b5fd, #8b5cf6)',
  navy:      '#1a0c38',
  navy2:     '#2a1257',
  navyMid:   '#4a2691',
  bgWhite:   '#ffffff',
  bgLavender:'#f6f4fd',
  bgFaint:   '#efedf6',
  green:     '#22c55e',
  greenDeep: '#16a34a',
  greenLite: '#dcfce7',
  yellow:    '#fbbf24',
};

const ArrowIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, flexShrink: 0 }}>
    <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const LinkedInIcon = ({ size = 20, fill = '#0A66C2' }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, fill, flexShrink: 0 }}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);
// Brand logo mark — the same official asset + pattern every other page uses
// (frontend/public/images/brand/), so it's pixel-identical everywhere rather
// than this page rendering its own coded text+icon stand-in. `height` sets
// the rendered size; width is left to `auto` so the image's own aspect
// ratio is preserved and it's never stretched/distorted.
const Logo = ({ height = 28 }) => (
  <img
    src="/images/brand/successlane-logo-mark.png"
    alt="SuccessLane"
    className="sl-logo"
    // aspectRatio (real file is 597x90) reserves the correct width up front
    // instead of the browser guessing 0 until it loads — a smaller version
    // of the same layout-shift issue as the bigger section images below.
    style={{ height, width: 'auto', display: 'block', aspectRatio: '597 / 90' }}
  />
);

// Founders — spec content. Chandni only: Divya's bio card moved to the
// Strategic Advisors ("Brain Trust") section below, formatted like Jai/
// Vishal's cards there but keeping her actual "Co-Founder" role label.
// `photo` is served from public/images/founders/*.jpg — if the file doesn't
// exist yet the initials-avatar fallback kicks in (see the <Avatar> helper
// below and the hero portrait).
const founders = [
  {
    initials: 'CG',
    photo: '/images/founders/chandni.jpg',
    // Actual file is 900x1352 — set as a real aspect-ratio on both places
    // this photo renders at full width/auto-height (hero portrait, founder
    // spotlight) so the browser reserves the correct box *before* the file
    // loads. Without this, the image collapses to near-zero height until
    // it arrives, then the whole page reflows/jumps as it snaps to full
    // size — a real, measurable cause of "stuck and slow" scrolling, not
    // just a network-speed issue.
    aspectRatio: 900 / 1352,
    name: 'Chandni Gupta',
    role: 'Co-Founder · Recruitment & Strategy',
    linkedin: 'https://linkedin.com/in/chandniguptabullzeye',
    tags: ['Executive Search', 'FMCG', 'Manufacturing', 'D2C', 'Leadership & Diversity'],
    bio: [
      `Chandni has spent nearly a decade in executive search and leadership hiring. As founder of Bullzeye Consulting, she's partnered with India's leading organisations to place senior talent across marketing, finance, product, consumer sales, and digital roles. Her team maintains a database of over 4 lakh profiles with a 22% candidate conversion rate and less than 10% offer drop-outs.`,
      `Jombay HR100under40 2025 honoree. MBA in HR from ITM. Currently pursuing the Goldman Sachs programme at IIM Ahmedabad.`,
    ],
    avatarBg: '#ede9fe',
    avatarColor: c.purpleDeep,
  },
];

// Strategic Advisors — "The Brain Trust" section.
// `role` renders under each name — defaults to "Strategic Advisor" in the
// JSX below when not set, so Jai/Vishal don't need it spelled out here;
// Divya overrides it to "Co-Founder" since that's her actual role, even
// though her card now lives in this section, formatted the same way.
// `photo` is served from public/images/advisors/*.jpg (or, for Divya,
// the existing founders/ photo — same fallback pattern: if the file isn't
// there yet, the initials-avatar placeholder shows instead).
const advisors = [
  {
    initials: 'DP',
    photo: '/images/founders/divya.jpg',
    name: 'Divya R Palyam',
    role: 'Strategic Advisor',
    linkedin: '#',
    bio: `Divya's career spans banking and recruiting, giving her a first-hand view of how decision-makers in competitive sectors actually evaluate candidates. At Successlane she leads operations, recruiter network onboarding, and quality assurance on every certified profile.`,
    avatarBg: '#e0e7ff',
    avatarColor: '#4338ca',
  },
  {
    initials: 'JB',
    photo: '/images/advisors/jai-balan.jpg',
    name: 'Jai Balan',
    linkedin: 'https://www.linkedin.com/in/jaibalan/',
    bio: `Jai has spent over two decades building talent and leadership pipelines at Marico, Colgate-Palmolive, and the Aditya Birla Group, most recently as Head of HR at Bharti AXA Life Insurance. He is an ICF-certified executive coach and works with professionals on career direction and self-awareness.`,
    avatarBg: '#ede9fe',
    avatarColor: c.purpleDeep,
  },
  {
    initials: 'VM',
    photo: '/images/advisors/vishal-makkar.jpg',
    name: 'Vishal Makkar',
    linkedin: 'https://www.linkedin.com/in/vishalmakkar1986/',
    bio: `Vishal heads talent acquisition and talent management at Zydus Wellness, and also leads the HR function at Comfort Click through its post-acquisition integration. He brings a hiring-side view of how large consumer organisations screen and select senior talent.`,
    avatarBg: '#e0e7ff',
    avatarColor: '#4338ca',
  },
];

/**
 * Circular avatar that overlays an <img> on top of an initials placeholder.
 * If the image fails to load (file not uploaded yet, wrong extension, 404),
 * the onError handler hides the <img> and the initials-with-coloured-bg
 * placeholder remains visible. No broken-image icon ever shows.
 */
const Avatar = ({ size, bg, color, initials, photo, fontSize, borderStyle }) => (
  <div style={{ position: 'relative', width: size, height: size, borderRadius: '50%', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: fontSize || size * 0.36, flexShrink: 0, overflow: 'hidden', ...(borderStyle || {}) }}>
    {initials}
    {photo && (
      <img
        src={photo}
        alt=""
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    )}
  </div>
);

const About = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);

  // Same IntersectionObserver fade-in as the landing page
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !('IntersectionObserver' in window)) return;
    const blocks = Array.from(root.children).slice(1); // skip trust-bar (renders instant)
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.style.opacity = '1';
            e.target.style.transform = 'none';
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -5% 0px' }
    );
    blocks.forEach((el) => {
      el.style.transition = 'opacity .8s cubic-bezier(.16,1,.3,1), transform .8s cubic-bezier(.16,1,.3,1)';
      el.style.opacity = '0';
      el.style.transform = 'translateY(34px)';
      io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  const goAnalyze = () => navigate('/analyze');

  return (
    <div ref={rootRef} style={{ fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, sans-serif", color: c.text, lineHeight: 1.6, background: c.bgWhite, WebkitFontSmoothing: 'antialiased' }}>

      <style>{`
        .sl-cta-primary, .sl-nav-cta { transition: transform .18s, box-shadow .18s; }
        .sl-cta-primary:hover, .sl-nav-cta:hover { transform: translateY(-1px); box-shadow: 0 18px 40px rgba(124,58,237,.55); }
        /* See NewLanding.jsx for the full explanation — flex rows with text+
           icon children default to min-width:auto (their full content width)
           and silently overflow rather than wrap on narrow screens. */
        .sl-cta-primary, .sl-nav-cta { flex-wrap: wrap; min-width: 0; }
        .sl-nav-cta-short { display: none; }

        /* Same class of bug, but for CSS Grid: a grid item's default
           min-width:auto resolves to its *automatic minimum size*, which
           can force a 1fr track to grow past the space actually available
           — the item (and every line of text inside it) then renders wider
           than the viewport and silently clips off-screen. min-width:0
           lets the item shrink to its assigned track size so text wraps. */
        .sl-about-hero-grid > *, .sl-story-grid > *, .sl-advisors-grid > * { min-width: 0; }
        .sl-nav-link:hover { color: ${c.purple}; }

        /* Hero founder photo — lifts slightly + deepens the shadow on hover,
           like picking the photo up off the surface it's resting on. The
           shadow lives on the photo div itself (first child), not the
           outer wrapper, since there's no card chrome around it anymore. */
        .sl-hero-founder-card > div:first-child { transition: transform .25s cubic-bezier(.16,1,.3,1), box-shadow .25s; }
        .sl-hero-founder-card:hover > div:first-child {
          transform: translateY(-6px) scale(1.015);
          box-shadow: 0 14px 22px -4px rgba(2,1,10,.6), 0 50px 74px -16px rgba(2,1,10,.85);
        }

        /* Strategic Advisors — static cards, no auto-scroll/marquee (spec:
           keep it static, not constantly moving). Bio is always visible. */
        .sl-advisor-card {
          transform: rotate(var(--tilt, 0deg)) translateY(var(--offy, 0px));
          transition: transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s;
        }
        /* "Picking the card up" — straightens, lifts well clear of its
           neighbours, and the shadow deepens + tightens as if it's now
           closer to the surface below it rather than resting flat on it. */
        .sl-advisor-card:hover {
          transform: rotate(0deg) translateY(-14px) scale(1.04);
          box-shadow: 0 34px 54px -14px rgba(8,3,20,.65), 0 8px 16px rgba(8,3,20,.35);
          z-index: 5;
        }
        @media (max-width: 860px) {
          .sl-about-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          /* The fanned "on a desk" tilt only reads correctly in a horizontal
             row — once it collapses to a single column, flatten it back out
             rather than stacking three individually-tilted cards. */
          .sl-advisors-grid { grid-template-columns: 1fr !important; max-width: 340px !important; }
          .sl-advisor-card { transform: none !important; }
          .sl-desk-shadow { display: none !important; }
          .sl-story-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .sl-founder-spotlight { grid-template-columns: 1fr !important; gap: 44px !important; }
          .sl-how-we-work { grid-template-columns: 1fr !important; gap: 32px !important; }
          .sl-promise-header { grid-template-columns: 1fr !important; gap: 32px !important; text-align: center; }
          .sl-nav-hide { display: none !important; }
        }
        @media (max-width: 640px) {
          section { padding-left: 18px !important; padding-right: 18px !important; overflow-x: clip; }
          nav > div { padding: 0 14px !important; height: 60px !important; gap: 10px !important; }
          .sl-logo { font-size: 19px !important; white-space: nowrap !important; }
          .sl-nav-cta { padding: 10px 14px !important; font-size: 13px !important; gap: 5px !important; white-space: nowrap !important; }
          h1 { font-size: clamp(28px, 8vw, 40px) !important; letter-spacing: -1px !important; }
          h2 { font-size: clamp(24px, 6.5vw, 32px) !important; letter-spacing: -.8px !important; }
          h3 { font-size: 17px !important; }
          .sl-cta-primary { width: 100%; justify-content: center !important; padding: 15px 20px !important; text-align: center; white-space: normal !important; }
          /* Chandni's hero photo: shrink a bit on phones */
          .sl-hero-founder-card { width: 220px !important; }
          .sl-hero-founder-card span { font-size: 52px !important; }
          /* Founder spotlight stat strip: 2-up instead of 4-in-a-row */
          .sl-founder-stats > div { flex: 1 1 45% !important; }
          /* Company info table: stack label + value on phones */
          section > div > div[style*="grid-template-columns: auto 1fr"] { grid-template-columns: 1fr !important; gap: 4px 0 !important; }
          section > div > div[style*="grid-template-columns: auto 1fr"] > div:nth-child(odd) { margin-top: 12px; }
          /* Footer: stack copyright over links */
          footer > div { flex-direction: column !important; align-items: flex-start !important; }
        }
        @media (max-width: 480px) {
          .sl-nav-cta-full { display: none !important; }
          .sl-nav-cta-short { display: inline !important; }
          .sl-logo { font-size: 17px !important; }
        }
      `}</style>

      {/* Trust bar */}
      <div style={{ background: c.navy, color: '#fff', textAlign: 'center', fontSize: 13, fontWeight: 600, letterSpacing: '.4px', padding: '11px 18px' }}>
        Built by LinkedIn-certified recruiters
      </div>

      {/* ============== Nav + Hero (dark region) ============== */}
      <div style={{ background: `radial-gradient(1200px 700px at 50% -100px, ${c.navyMid} 0%, ${c.navy2} 42%, ${c.navy} 100%)`, backgroundColor: c.navy, padding: '18px 18px 96px' }}>
        <nav style={{ position: 'sticky', top: 12, zIndex: 30, maxWidth: 1240, margin: '0 auto', background: '#fff', borderRadius: 22, boxShadow: '0 20px 50px rgba(20,8,50,.35)' }}>
          <div style={{ padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 74 }}>
            <a href="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
              <Logo />
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
              <a href="/#pricing" className="sl-nav-link sl-nav-hide" style={{ color: c.textMid, fontSize: 15, fontWeight: 600, textDecoration: 'none' }}>Pricing</a>
              <a href="/about" className="sl-nav-link sl-nav-hide" style={{ color: c.purple, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>About</a>
              <button onClick={goAnalyze} className="sl-nav-cta" style={{ background: c.gradient, color: '#fff', padding: '12px 22px', borderRadius: 13, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: '0 8px 20px rgba(124,58,237,.4)', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                <span className="sl-nav-cta-full">Get Free Evaluation</span>
                <span className="sl-nav-cta-short">Evaluate</span>
                <ArrowIcon />
              </button>
            </div>
          </div>
        </nav>

        <section style={{ maxWidth: 1180, margin: '0 auto', padding: '80px 24px 0' }}>
          <div className="sl-about-hero-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.15fr) minmax(0,1fr)', gap: 64, alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: 'clamp(40px,5.4vw,66px)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-2px', color: '#fff', marginBottom: 22 }}>
                We're{' '}
                <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SuccessLane.</span>
              </h1>
              <p style={{ fontSize: 20, color: 'rgba(255,255,255,.85)', lineHeight: 1.5, marginBottom: 22, fontWeight: 500 }}>
                A team of recruiters who got tired of watching good people get skipped.
              </p>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,.7)', lineHeight: 1.75, marginBottom: 32 }}>
                We spent over a decade hiring talent into India's leading companies. The same problem kept showing up — qualified, capable professionals getting overlooked because their LinkedIn didn't say what recruiters were actually searching for. So we stopped complaining about it and built something to fix it.
              </p>
              <button onClick={goAnalyze} className="sl-cta-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: c.gradient, color: '#fff', padding: '16px 34px', borderRadius: 14, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 14px 34px rgba(124,58,237,.45)', fontFamily: 'inherit' }}>
                Get Your Free Evaluation <ArrowIcon size={18} />
              </button>
            </div>

            {/* Chandni's hero portrait — no card chrome (no cream border/
                background framing it) so the photo reads as embedded
                directly into the dark hero, not as a bordered tile sitting
                on top of it. Depth comes entirely from a strong, directional
                drop shadow instead of a border — a close, dark contact
                shadow (grounds it right at the edge) plus a big soft ambient
                one beneath (real distance/depth), both offset down-right as
                if lit from the upper-left, which is what actually reads as
                "3D" rather than just "blurry". Caption sits directly below
                in the hero's own white/light text — no separate card bg.

                Solo now — Divya's card moved to Strategic Advisors below
                (kept `founders.map()` rather than hardcoding to `founders[0]`
                so this still degrades gracefully if the array ever gains a
                second entry again). `alignItems: 'flex-start'` on the row
                stops flex's default `stretch` from forcing every card to
                match the tallest sibling's height — not load-bearing with
                just one card, but keeps this safe if that changes.

                IMPORTANT: the photo has NO fixed aspect-ratio — it sits in
                normal flow at width:100%/height:auto, so its rendered height
                is entirely driven by the source image's own natural aspect
                ratio. That means the *entire* photo always shows, uncropped,
                at whatever shape it actually is: swap in a taller/fuller
                portrait photo later and it grows to fit automatically — no
                code change needed here again.
                Falls back to an initials tile (absolutely centered, with a
                floor height via minHeight) if a photo 404s. */}
            <div className="sl-hero-founder-cards" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 28 }}>
              {founders.map((f) => (
                // Sized up from the original 2-card width (240px) now that
                // it's carrying this column alone — fills the space the
                // right column actually has instead of looking small and
                // lost with a big empty margin around it.
                <div key={f.name} className="sl-hero-founder-card" style={{ width: 340 }}>
                  <div
                    style={{
                      position: 'relative', width: '100%', minHeight: 200,
                      background: f.avatarBg, borderRadius: 14, overflow: 'hidden',
                      boxShadow: '0 10px 16px -4px rgba(2,1,10,.55), 0 40px 60px -18px rgba(2,1,10,.75)',
                    }}
                  >
                    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: f.avatarColor, fontWeight: 800, fontSize: 64 }}>{f.initials}</span>
                    <img
                      src={f.photo}
                      alt={f.name}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      style={{ position: 'relative', display: 'block', width: '100%', height: 'auto', aspectRatio: f.aspectRatio }}
                    />
                  </div>
                  <div style={{ textAlign: 'center', paddingTop: 16 }}>
                    {/* Indie Flower stays deliberately here — the one
                        intentional exception to "brand font everywhere"
                        elsewhere on this page. Single-weight font on Google
                        Fonts, so "bold" is the browser's synthetic-bold
                        fallback rather than a true bold cut. */}
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 32, fontFamily: "'Indie Flower', cursive" }}>{f.name.split(' ')[0]}</div>
                    <div style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginTop: 2, fontFamily: "'Indie Flower', cursive" }}>Co-Founder</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ============== Section 2: Where It All Started ============== */}
      <section style={{ padding: '96px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 780, margin: '0 auto 56px' }}>
            <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 2, color: c.purple, fontWeight: 700, marginBottom: 14 }}>WHERE IT ALL STARTED</div>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px' }}>
              From the recruiter's search box to{' '}
              <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>your LinkedIn profile.</span>
            </h2>
          </div>

          <div className="sl-story-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 24, maxWidth: 1080, margin: '0 auto' }}>
            {[
              { n: '01', title: 'The problem we kept seeing', body: `Between us, we've reviewed over 4 lakh profiles and placed hundreds of professionals into leadership roles across tech, FMCG, banking, pharma, and consulting. The pattern was always the same: strong careers, invisible profiles. Not because the work wasn't there — because it wasn't written in the words recruiters type into the search box.` },
              { n: '02', title: 'What we decided to do about it', body: `We started writing LinkedIn profiles the way we actually search for candidates. With the exact keywords, structure, and positioning that makes a recruiter stop scrolling and pick up the phone. Then we had other recruiters from the same industry review and certify every profile before it went live.` },
              { n: '03', title: 'What it became', body: `Today, Successlane is a full platform. Every profile is written by a LinkedIn expert who understands how recruiter search works, then certified by an active recruiter from your industry who hires for roles like yours. 1,928 professionals have used it. The average salary hike reported by our last 100 rewrites is 38%.` },
            ].map((block) => (
              <div key={block.n} style={{ background: c.bgLavender, borderRadius: 22, padding: 32, borderTop: `3px solid ${c.purple}` }}>
                <div style={{ fontSize: 34, fontWeight: 800, color: c.purple, letterSpacing: '-1px', marginBottom: 14 }}>{block.n}</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: c.text, marginBottom: 12, lineHeight: 1.3 }}>{block.title}</h3>
                <p style={{ fontSize: 14, color: c.text, lineHeight: 1.7 }}>{block.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== Section 3: How We Work ============== */}
      <section style={{ padding: '96px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          {/* Image column widened (1.3fr vs 1fr) and gap tightened (64->48)
              to size the photo up noticeably, per feedback — source file is
              1400px wide, comfortably covers this column at retina density
              without any visible quality loss. */}
          <div className="sl-how-we-work" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 48, alignItems: 'center' }}>
            <img
              src="/images/about/how-we-work.jpg"
              alt="A recruiter reviewing a candidate's LinkedIn profile, taking notes on strengths and fit"
              loading="lazy"
              style={{ width: '100%', display: 'block', borderRadius: 24, boxShadow: '0 30px 60px rgba(30,15,60,.15)', aspectRatio: '1400 / 933' }}
            />
            <div>
              <div style={{ fontSize: 15, textTransform: 'uppercase', letterSpacing: 2, color: c.purple, fontWeight: 700, marginBottom: 14, fontFamily: "'Poppins', sans-serif" }}>HOW WE WORK</div>
              <h2 style={{ fontSize: 'clamp(32px,4.4vw,50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px', marginBottom: 20, fontFamily: "'Poppins', sans-serif", color: c.purple }}>
                Written By Recruiters.
              </h2>
              <p style={{ fontSize: 17, color: c.text, lineHeight: 1.8, fontFamily: "'Poppins', sans-serif" }}>
                Every profile is written and certified by people who've spent years on the other side of the hiring table — not freelancers, not templates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Section 4: The Founder — editorial spotlight ==============
          Deliberately NOT the generic "avatar + name + role + tag pills +
          paragraph in a rounded shadow card" pattern — that's the template
          every AI-generated team section defaults to. Instead this borrows
          from magazine/editorial layout conventions: an asymmetric photo/
          text split, a layered colour-block behind the photo for depth, a
          giant faint initials watermark for scale and intent, real numbers
          pulled straight from her existing bio reframed as a stat strip
          (not decorative tag pills), and oversized display typography
          driving the hierarchy instead of a bordered box. */}
      <section style={{ padding: '110px 24px', background: c.bgWhite, position: 'relative', overflow: 'hidden' }}>
        {/* Giant faint initials behind the layout — pure decoration, marks
            this as a considered spread rather than a component template. */}
        <div aria-hidden="true" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-52%)', fontSize: 'min(48vw, 620px)', fontWeight: 800, color: c.bgLavender, lineHeight: 1, whiteSpace: 'nowrap', userSelect: 'none', pointerEvents: 'none', zIndex: 0 }}>
          {founders[0].initials}
        </div>

        <div style={{ maxWidth: 1180, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 3, color: c.purple, fontWeight: 700, marginBottom: 40 }}>— The Founder</div>

          <div className="sl-founder-spotlight" style={{ display: 'grid', gridTemplateColumns: '0.85fr 1.15fr', gap: 72, alignItems: 'center' }}>
            {/* Left: layered photo — a solid gradient block sits behind and
                peeks out at the corner, a common editorial depth trick, in
                place of a flat drop-shadow. */}
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: 28, left: -28, right: -28, bottom: -28, background: c.gradient, borderRadius: 30, zIndex: 0 }} />
              <div style={{ position: 'relative', zIndex: 1, borderRadius: 26, overflow: 'hidden', boxShadow: '0 44px 84px -24px rgba(20,8,50,.4)' }}>
                <img src={founders[0].photo} alt={founders[0].name} loading="lazy" style={{ width: '100%', display: 'block', aspectRatio: founders[0].aspectRatio }} />
              </div>
            </div>

            {/* Right: name, credentials-as-stats, bio, LinkedIn */}
            <div>
              <h2 style={{ fontSize: 'clamp(38px,4.8vw,60px)', fontWeight: 800, letterSpacing: '-1.8px', lineHeight: 1.02, marginBottom: 8, color: c.text }}>
                {founders[0].name}
              </h2>
              <div style={{ fontSize: 16, fontWeight: 700, color: c.purple, marginBottom: 32 }}>{founders[0].role}</div>

              {/* Stat strip — the actual numbers already in her bio below,
                  pulled forward as the credential proof instead of generic
                  "Executive Search / FMCG / D2C" tag pills. */}
              <div className="sl-founder-stats" style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 36, borderTop: `1px solid ${c.bgFaint}`, borderBottom: `1px solid ${c.bgFaint}` }}>
                {[
                  { n: '~10', l: 'Years in Executive Search' },
                  { n: '4L+', l: 'Profiles Reviewed' },
                  { n: '22%', l: 'Candidate Conversion' },
                  { n: '<10%', l: 'Offer Drop-outs' },
                ].map((s, i) => (
                  <div key={s.l} style={{ flex: '1 1 130px', padding: '20px 18px 20px 0', paddingLeft: i > 0 ? 18 : 0, borderLeft: i > 0 ? `1px solid ${c.bgFaint}` : 'none' }}>
                    <div style={{ fontSize: 27, fontWeight: 800, color: c.text, letterSpacing: '-.5px' }}>{s.n}</div>
                    <div style={{ fontSize: 12, color: c.text, marginTop: 3, fontWeight: 600, lineHeight: 1.4 }}>{s.l}</div>
                  </div>
                ))}
              </div>

              {founders[0].bio.map((p, i) => (
                <p key={i} style={{ fontSize: 16.5, color: c.text, lineHeight: 1.85, marginBottom: i < founders[0].bio.length - 1 ? 16 : 26 }}>{p}</p>
              ))}

              <a href={founders[0].linkedin} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: c.text, textDecoration: 'none', borderBottom: `2px solid ${c.purple}`, paddingBottom: 3 }}>
                <LinkedInIcon size={18} /> Connect with Chandni on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Section 4b: Strategic Advisors — "The Brain Trust" ============== */}
      <section style={{ padding: '96px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 780, margin: '0 auto 52px' }}>
            <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 2, color: c.purple, fontWeight: 700, marginBottom: 14 }}>THE BRAIN TRUST</div>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px' }}>
              Backed by people who've{' '}
              <span style={{ background: c.hilite, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>been in the room before.</span>
            </h2>
          </div>

          {/* Three cards fanned like photos casually set down on a desk —
              outer two tilted slightly inward toward the middle one ("facing"
              each other), each with its own footprint shadow grounding it to
              the surface. Tilt/offset live as CSS custom properties per card
              (--tilt / --offy) so :hover can cleanly override to "pick the
              card up" — straighten, lift, and pop the shadow — without
              fighting inline styles or needing !important anywhere. */}
          <div className="sl-advisors-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 40, maxWidth: 980, margin: '0 auto', alignItems: 'end' }}>
            {advisors.map((a, i) => {
              const desk = [
                { tilt: 4, offy: 14 },   // left — leans right, toward centre
                { tilt: -1, offy: -6 },  // middle — near-straight, slight imperfection
                { tilt: -4, offy: 10 },  // right — leans left, toward centre
              ][i] || { tilt: 0, offy: 0 };
              return (
                <div key={a.name} className="sl-desk-card-wrap" style={{ position: 'relative', paddingBottom: 20 }}>
                  {/* Contact shadow — a blurred ellipse "pooled" under the
                      card, not rotated with it (a shadow on a flat desk
                      stays flat regardless of how the object on it is
                      turned), which is what actually sells "resting on a
                      surface" rather than "floating with a blur behind it". */}
                  <div aria-hidden="true" className="sl-desk-shadow" style={{ position: 'absolute', left: '10%', right: '10%', bottom: 4, height: 26, borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(8,3,20,.55) 0%, rgba(8,3,20,.22) 55%, transparent 78%)', filter: 'blur(6px)', zIndex: 0 }} />
                  <div
                    className="sl-advisor-card"
                    style={{
                      '--tilt': `${desk.tilt}deg`,
                      '--offy': `${desk.offy}px`,
                      position: 'relative',
                      zIndex: 1,
                      background: `linear-gradient(160deg, ${c.navyMid} 0%, ${c.navy} 100%)`,
                      borderRadius: 20,
                      padding: '34px 26px',
                      textAlign: 'center',
                      boxShadow: '0 22px 36px -12px rgba(8,3,20,.55), 0 4px 10px rgba(8,3,20,.3)',
                    }}
                  >
                    <Avatar
                      size={104}
                      bg={a.avatarBg}
                      color={a.avatarColor}
                      initials={a.initials}
                      photo={a.photo}
                      fontSize={38}
                      borderStyle={{ boxShadow: '0 10px 26px rgba(0,0,0,.3)', border: '3px solid rgba(255,255,255,.2)', margin: '0 auto' }}
                    />

                    <div style={{ marginTop: 20, fontSize: 18, fontWeight: 800, color: '#fff' }}>{a.name}</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,.65)', fontWeight: 600, marginTop: 3 }}>{a.role || 'Strategic Advisor'}</div>

                    <a
                      href={a.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 13, color: '#8ec2ff', fontWeight: 600, textDecoration: 'none' }}
                    >
                      <LinkedInIcon size={16} fill="#8ec2ff" /> LinkedIn
                    </a>

                    <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,.82)', lineHeight: 1.7, marginTop: 16, textAlign: 'left' }}>{a.bio}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== Section 5: What We Promise ============== */}
      {/* Coded checklist cards replaced with the designed graphic — it
          already shows the same 4 promises visually, so keeping both would
          just repeat the same content twice back to back. Heading stays as
          real text (not baked into the image) on the left; CTA stays below,
          centered, since the reference image didn't include one and it's a
          conversion-critical element not worth dropping silently. */}
      <section style={{ padding: '96px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div className="sl-promise-header" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.3fr)', gap: 28, alignItems: 'center', marginBottom: 56 }}>
            <div>
              <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 2, color: c.purple, fontWeight: 700, marginBottom: 14 }}>WHAT WE PROMISE</div>
              <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.4px' }}>
                Every Successlane rewrite is:
              </h2>
            </div>
            <img
              src="/images/about/what-we-promise.jpg"
              alt="What we promise: written by a LinkedIn expert and certified by a named recruiter, tailored to your target role, delivered with a 100-day action plan, backed by a quality guarantee"
              loading="lazy"
              style={{ width: '100%', display: 'block', borderRadius: 24, boxShadow: '0 30px 60px rgba(30,15,60,.15)', aspectRatio: '1400 / 933' }}
            />
          </div>

          <div style={{ textAlign: 'center' }}>
            <button onClick={goAnalyze} className="sl-cta-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: c.gradient, color: '#fff', padding: '17px 40px', borderRadius: 14, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 14px 34px rgba(124,58,237,.45)', fontFamily: 'inherit' }}>
              Get Your Free Evaluation <ArrowIcon size={18} />
            </button>
            <p style={{ fontSize: 13, color: c.text, marginTop: 14 }}>Free. No card. Two minutes.</p>
          </div>
        </div>
      </section>

      {/* ============== Section 6: Company Information ============== */}
      <section style={{ padding: '80px 24px', background: c.bgWhite }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ background: c.bgLavender, borderRadius: 22, padding: 40, borderLeft: `4px solid ${c.purple}` }}>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: c.text, marginBottom: 22 }}>Company information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '12px 24px', fontSize: 14, color: c.text, lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, color: c.text }}>Legal name</div>
              <div>Success Lane (a registered partnership firm)</div>
              <div style={{ fontWeight: 700, color: c.text }}>Operating as</div>
              <div>Successlane</div>
              <div style={{ fontWeight: 700, color: c.text }}>Registered office</div>
              <div>21/22, T2, 3rd Floor, Girinagar 2nd Phase, Bangalore, Karnataka 560085, India</div>
              <div style={{ fontWeight: 700, color: c.text }}>Email</div>
              <div><a href="mailto:contact@successlane.io" style={{ color: c.purple, textDecoration: 'none', fontWeight: 600 }}>contact@successlane.io</a></div>
              <div style={{ fontWeight: 700, color: c.text }}>Phone</div>
              <div><a href="tel:+917259444336" style={{ color: c.purple, textDecoration: 'none', fontWeight: 600 }}>+91 72594 44336</a></div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer — same as landing */}
      <footer style={{ padding: '40px 24px 56px', background: c.bgWhite, borderTop: `1px solid ${c.bgFaint}` }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <Logo height={18} />
            <span style={{ fontSize: 13, color: c.text }}>© {new Date().getFullYear()} Successlane. All rights reserved.</span>
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <a href="/about"        style={{ fontSize: 13, color: c.text, textDecoration: 'none' }}>About</a>
            <a href="/privacy"      style={{ fontSize: 13, color: c.text, textDecoration: 'none' }}>Privacy</a>
            <a href="/terms"        style={{ fontSize: 13, color: c.text, textDecoration: 'none' }}>Terms</a>
            <a href="/refund"       style={{ fontSize: 13, color: c.text, textDecoration: 'none' }}>Refund</a>
            <a href="/cancellation" style={{ fontSize: 13, color: c.text, textDecoration: 'none' }}>Cancellation</a>
            <a href="/shipping"     style={{ fontSize: 13, color: c.text, textDecoration: 'none' }}>Shipping</a>
            <a href="mailto:contact@successlane.io" style={{ fontSize: 13, color: c.text, textDecoration: 'none' }}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
